// src/components/tokenPage/valuation/ValuationInsightsCard.tsx

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { MetricBox } from "@/components/tokenPage/shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useTokenFundamentals } from "@/contexts/token";
import { 
  fetchTokenCategoryMap, 
  fetchTokensCurrentRatios,
} from "@/services/queries/tokens/tokensScreenerQueries";
import { percentileRank, cvPercent, filterValid } from "@/lib/metrics";

type Range = '30d' | '90d' | '180d' | '365d';

/**
 * ValuationInsightsCard - Percentiles, stability, correlation, momentum
 * 
 * Uses TokenFundamentalsContext for data.
 * Sector percentile still requires fetching sector peers (external data).
 */
const ValuationInsightsCard: React.FC = () => {
  const [range, setRange] = useState<Range>('90d');
  const windowDays = range === '30d' ? 30 : range === '90d' ? 90 : range === '180d' ? 180 : 365;
  
  const { ticker, fees, mcToFeesHistory, current, isLoading: fundamentalsLoading } = useTokenFundamentals();

  // Compute metrics from context data
  const { feesCvPct, mcFeesMomentumPct } = useMemo(() => {
    const feesSlice = fees.values.slice(-windowDays);
    const mcFeesSlice = mcToFeesHistory.values.slice(-windowDays);
    
    // Fees CV
    const feesCvPct = cvPercent(feesSlice);
    
    // MC/Fees momentum
    const validMcFees = filterValid(mcFeesSlice);
    let mcFeesMomentumPct: number | null = null;
    if (validMcFees.length > 1) {
      const curr = validMcFees[validMcFees.length - 1]!;
      const prev = validMcFees[0]!;
      if (prev !== 0) {
        mcFeesMomentumPct = ((curr - prev) / prev) * 100;
      }
    }
    
    return { feesCvPct, mcFeesMomentumPct };
  }, [fees.values, mcToFeesHistory.values, windowDays]);

  // Fetch sector peers for percentile calculation (this is external data)
  const categoryMapQ = useQuery({
    queryKey: ["tokens", "category-map"],
    queryFn: ({ signal }) => fetchTokenCategoryMap({ signal }),
    staleTime: 1000 * 60 * 60,
  });

  const peerTickersCsv = useMemo(() => {
    const map = categoryMapQ.data as Record<string, number[]> | undefined;
    if (!map) return ticker;
    const baseCats = map[ticker] || [];
    if (!baseCats.length) return ticker;
    const firstCat = baseCats[0] as number;
    const peers = Object.entries(map)
      .filter(([tk, cats]) => tk.toUpperCase() !== ticker && Array.isArray(cats) && (cats as number[]).includes(firstCat))
      .map(([tk]) => tk.toUpperCase());
    const all = [ticker, ...peers];
    return all.slice(0, 200).join(',');
  }, [categoryMapQ.data, ticker]);

  const sectorRatiosQ = useQuery({
    queryKey: ["tokens", "ratios", "mc_fees", peerTickersCsv],
    queryFn: ({ signal }) => fetchTokensCurrentRatios({ 
      tickersCsv: peerTickersCsv, 
      ratioKeysCsv: 'ratio:marketCapToAnnualisedFees', 
      signal 
    }),
    staleTime: 60_000,
    enabled: !!peerTickersCsv,
  });

  // Calculate sector percentile
  const sectorPct = useMemo(() => {
    const tokens = sectorRatiosQ.data?.tokens || [];
    const sample = tokens
      .map(t => t.metrics?.['ratio:marketCapToAnnualisedFees'])
      .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    
    if (current.mcToFees == null || sample.length === 0) return null;
    return percentileRank(current.mcToFees, sample);
  }, [sectorRatiosQ.data, current.mcToFees]);

  const isLoading = fundamentalsLoading || categoryMapQ.isLoading || sectorRatiosQ.isLoading;

  // Hide if no data available
  const hasData = sectorPct != null || feesCvPct != null || mcFeesMomentumPct != null;
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <DataCard
      title="Valuation insights"
      subtitle="Percentiles, stability, momentum"
      icon="lightbulb"
      iconColor="text-amber-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={<TimeRangePicker options={['30d','90d','180d','365d'] as const} value={range} onChange={setRange} theme="blue" />}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <MetricBox label="Market Cap / Fees percentile (sector)" value={sectorPct} fmt="int" suffix=" pctl" />
        <MetricBox label={`Fees stability (CV) (${range})`} value={feesCvPct} fmt="pct1" />
        <MetricBox label={`Market Cap / Fees momentum (${range})`} value={mcFeesMomentumPct} fmt="pct1" toneBySign />
      </div>
    </DataCard>
  );
};

export default ValuationInsightsCard;
